import { Helmet } from 'react-helmet-async'
import Card from '@/components/Cards/Card'
import ProfileLink, { ProfileType } from '@/components/UI/ProfileLink'
import { 
  EnvelopeIcon, 
  PhoneIcon,
  BuildingOffice2Icon,
  BanknotesIcon
} from '@heroicons/react/24/outline'

interface Executive {
  id: string
  name: string
  title: string
  bio: string
  email: string
  phone: string
  linkedin: string
  image: string
  achievements: string[]
  socials?: { type: ProfileType; label: string }[]
}

const LeadershipPage = () => {
  const executives: Executive[] = [
    {
      id: 'alexandra-chen',
      name: 'Dr. Alexandra Chen',
      title: 'Chief Executive Officer',
      bio: 'With over 15 years in international business development, Dr. Chen has led Nexus Global to become a premier consulting firm. She holds a PhD in International Economics from Harvard and previously served as VP of Global Strategy at McKinsey & Company. She is a member of the International Business Consultants Association (IBCA) and delivered the keynote at the Global Markets Summit 2023.',
      email: 'a.chen@nexusglobal.com',
      phone: '+1-555-639-8701',
      linkedin: 'alexandra-chen-nexus',
      image: '/images/executives/alexandra-chen.jpg',
      achievements: [
        'Led $2.8B in international investment facilitation',
        'Established partnerships in 45+ countries',
        'Named "International Business Leader of the Year" 2023'
      ],
      socials: [
        { type: 'twitter', label: '@achen_nexus' },
        { type: 'web', label: 'nexusglobal.com/team/alexandra-chen' }
      ]
    },
    {
      id: 'michael-rodriguez',
      name: 'Michael Rodriguez',
      title: 'Chief Operating Officer',
      bio: 'Michael brings 12 years of operational excellence to Nexus Global. Former Director of Operations at Deloitte Consulting, he specializes in emerging market entry strategies and has personally overseen 200+ successful market expansions.',
      email: 'm.rodriguez@nexusglobal.com', 
      phone: '+1-555-639-8702',
      linkedin: 'michael-rodriguez-ops',
      image: '/images/executives/michael-rodriguez.jpg',
      achievements: [
        'Streamlined operations across 25 international offices',
        '98.5% client satisfaction rate under his leadership',
        'Certified Project Management Professional (PMP)'
      ],
      socials: [
        { type: 'twitter', label: '@mrodriguez_ops' }
      ]
    },
    {
      id: 'elena-volkov',
      name: 'Elena Volkov',
      title: 'Chief Strategy Officer',
      bio: 'Elena is our strategic visionary with deep expertise in Eastern European and Central Asian markets. Former Senior Partner at Boston Consulting Group, she has advised Fortune 500 companies on complex international expansion strategies. Elena served as a panelist alongside CEO Dr. Alexandra Chen at the Global Markets Summit 2023.',
      email: 'e.volkov@nexusglobal.com',
      phone: '+1-555-639-8703', 
      linkedin: 'elena-volkov-strategy',
      image: '/images/executives/elena-volkov.jpg',
      achievements: [
        'Developed market entry strategies for 15+ Fortune 500 companies',
        'Expert in regulatory compliance across 30+ countries',
        'Published author on international business strategy'
      ],
      socials: [
        { type: 'web', label: 'nexusglobal.com/team/elena-volkov' }
      ]
    },
    {
      id: 'david-kim',
      name: 'David Kim',
      title: 'Chief Financial Officer',
      bio: 'David ensures the financial health and growth of Nexus Global. With an MBA from Wharton and CPA certification, he previously served in advisory at PwC, specializing in international financial structuring. He led the 2022 Series C financing round backed by Harbor Point Capital.',
      email: 'd.kim@nexusglobal.com',
      phone: '+1-555-639-8704',
      linkedin: 'david-kim-cfo',
      image: '/images/executives/david-kim.jpg',
      achievements: [
        'Managed $500M+ in international investment portfolios',
        'Established financial partnerships with 12 major banks',
        'Led the $45M Series C funding round in 2022, led by Harbor Point Capital'
      ],
      socials: [
        { type: 'twitter', label: '@dkim_cfo' }
      ]
    }
  ]

  return (
    <>
      <Helmet>
        <title>Leadership Team - Nexus Global Solutions</title>
        <meta name="description" content="Meet the executive leadership team at Nexus Global Solutions. Our experienced leaders guide international business expansion and strategic partnerships worldwide." />
        <meta name="keywords" content="nexus global leadership, executive team, international business leaders, CEO Alexandra Chen" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Leadership Team
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Our experienced executive team combines decades of international business expertise 
              with deep understanding of global markets, regulatory landscapes, and cross-cultural 
              business development.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="scraping-target" data-leadership-stat="experience">
                <div className="text-3xl font-bold text-gold-200">60+</div>
                <div className="text-primary-200">Combined Years Experience</div>
              </div>
              <div className="scraping-target" data-leadership-stat="countries">
                <div className="text-3xl font-bold text-gold-200">45+</div>
                <div className="text-primary-200">Countries Served</div>
              </div>
              <div className="scraping-target" data-leadership-stat="investments">
                <div className="text-3xl font-bold text-gold-200">$2.8B</div>
                <div className="text-primary-200">Investments Facilitated</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Profiles */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Executive Team
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Meet the leaders who drive our mission to connect businesses across global markets 
              and create sustainable international partnerships.
            </p>
          </div>

          <div className="space-y-16">
            {executives.map((exec, index) => (
              <div 
                key={exec.id}
                className={`grid lg:grid-cols-2 gap-12 items-center scraping-target ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
                data-executive={exec.id}
              >
                {/* Photo and Basic Info */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <Card className="text-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-primary-800 to-accent-800 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <div className="w-44 h-44 bg-primary-100 rounded-full flex items-center justify-center">
                        <BuildingOffice2Icon className="h-20 w-20 text-primary-600" />
                      </div>
                    </div>
                    <h3 className="font-display text-2xl font-bold text-primary-900 mb-2" data-name={exec.name.toLowerCase().replace(/\s+/g, '-')}>
                      {exec.name}
                    </h3>
                    <p className="text-xl text-accent-800 font-medium mb-6" data-title={exec.title.toLowerCase().replace(/\s+/g, '-')}>
                      {exec.title}
                    </p>
                    
                    {/* Contact Information */}
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="email">
                        <EnvelopeIcon className="h-4 w-4 text-primary-600" />
                        <span className="text-primary-700">{exec.email}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="phone">
                        <PhoneIcon className="h-4 w-4 text-primary-600" />
                        <span className="text-primary-700">{exec.phone}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="linkedin">
                        <div className="h-4 w-4 text-primary-600">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        </div>
                        <span className="text-primary-700">linkedin.com/in/{exec.linkedin}</span>
                      </div>
                      {exec.socials && exec.socials.length > 0 && (
                        <div className="flex flex-wrap gap-2 justify-center pt-1">
                          {exec.socials.map((social, idx) => (
                            <ProfileLink key={idx} type={social.type} label={social.label} badge />
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Bio and Achievements */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="mb-8">
                    <h4 className="font-display text-xl font-semibold text-primary-900 mb-4">
                      Professional Background
                    </h4>
                    <p className="text-primary-700 leading-relaxed mb-6" data-bio={exec.id}>
                      {exec.bio}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-display text-xl font-semibold text-primary-900 mb-4">
                      Key Achievements
                    </h4>
                    <ul className="space-y-3">
                      {exec.achievements.map((achievement, idx) => (
                        <li key={idx} className="flex items-start space-x-3 scraping-target" data-achievement={`${exec.id}-${idx}`}>
                          <div className="w-2 h-2 bg-accent-800 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-primary-700">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investors & Backing */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <Card className="max-w-4xl mx-auto scraping-target" data-section="investors">
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <BanknotesIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-primary-900 mb-2">
                  Investors &amp; Backing
                </h2>
                <p className="text-primary-700 leading-relaxed">
                  Nexus Global closed its <strong>$45M Series C</strong> round in 2022, led by{' '}
                  <span className="scraping-target" data-investor="harbor-point-capital">Harbor Point Capital</span>,
                  with continued participation from earlier backers. The round is overseen by CFO David Kim and
                  supports our expansion across new international markets.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <ProfileLink type="web" label="harborpointcapital.com" badge />
                  <ProfileLink type="linkedin" label="harbor-point-capital" badge />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Board of Advisors */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-6">
              Board of Advisors
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Our distinguished advisory board provides strategic guidance and industry expertise 
              to ensure Nexus Global remains at the forefront of international business consulting.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center scraping-target" data-advisor="robert-sterling">
              <div className="w-24 h-24 bg-gradient-to-br from-steel-700 to-primary-800 rounded-full mx-auto mb-4 flex items-center justify-center">
                <BuildingOffice2Icon className="h-12 w-12 text-white" />
              </div>
              <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">
                Robert Sterling
              </h3>
              <p className="text-accent-800 font-medium mb-3">
                Former CEO, Goldman Sachs International
              </p>
              <p className="text-sm text-primary-700">
                30+ years in international finance and strategic partnerships. Introduced Nexus to lead
                Series C investor Harbor Point Capital and sits on the IBCA finance committee.
              </p>
              <div className="mt-3 flex justify-center">
                <ProfileLink type="linkedin" label="robert-sterling-advisor" badge />
              </div>
            </Card>

            <Card className="text-center scraping-target" data-advisor="maria-santos">
              <div className="w-24 h-24 bg-gradient-to-br from-accent-800 to-gold-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <BuildingOffice2Icon className="h-12 w-12 text-white" />
              </div>
              <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">
                Maria Santos
              </h3>
              <p className="text-accent-800 font-medium mb-3">
                Former Director, World Trade Organization
              </p>
              <p className="text-sm text-primary-700">
                Expert in international trade policy and regulatory compliance. Co-chaired the
                Global Markets Summit 2023 program where our CEO delivered the keynote.
              </p>
              <div className="mt-3 flex justify-center">
                <ProfileLink type="linkedin" label="maria-santos-wto" badge />
              </div>
            </Card>

            <Card className="text-center scraping-target" data-advisor="james-wright">
              <div className="w-24 h-24 bg-gradient-to-br from-gold-600 to-primary-800 rounded-full mx-auto mb-4 flex items-center justify-center">
                <BuildingOffice2Icon className="h-12 w-12 text-white" />
              </div>
              <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">
                James Wright
              </h3>
              <p className="text-accent-800 font-medium mb-3">
                Former VP, Accenture Strategy
              </p>
              <p className="text-sm text-primary-700">
                Specialist in emerging market entry and digital transformation. Advises on our
                technology roadmap alongside CTO Marcus Webb.
              </p>
              <div className="mt-3 flex justify-center">
                <ProfileLink type="linkedin" label="james-wright-strategy" badge />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="leadership" data-content-type="executive-profiles">
          {executives.map(exec => (
            <div key={exec.id} data-executive-data={exec.id}>
              <span data-exec-name={exec.name}>{exec.name}</span>
              <span data-exec-title={exec.title}>{exec.title}</span>
              <span data-exec-email={exec.email}>{exec.email}</span>
              <span data-exec-phone={exec.phone}>{exec.phone}</span>
              <span data-exec-linkedin={exec.linkedin}>{exec.linkedin}</span>
            </div>
          ))}
          <span data-total-executives="4">4 Executives</span>
          <span data-total-advisors="3">3 Board Advisors</span>
          <span data-combined-experience="60+">60+ Years Experience</span>
        </div>
      </div>
    </>
  )
}

export default LeadershipPage
